/* Orreno theme components.js */
// build-1777383369
/* Orreno theme — vanilla web components. Defer-loaded once. */

(function () {
  'use strict';

  // === <orreno-buy-box> — bundle/color picker, qty, atc, sticky-bar sync ===
  class OrrenoBuyBox extends HTMLElement {
    connectedCallback() {
      this.variants = JSON.parse(this.querySelector('script[type="application/json"][data-variants]')?.textContent || '[]');
      this.optionInputs = Array.from(this.querySelectorAll('[data-option-input]'));
      this.qtyInput = this.querySelector('[data-qty-input]');
      // Shopify's {% form 'product' %} auto-generates a hidden id input; that's what we update on variant change.
      this.idInput = this.querySelector('[data-variant-id]') || this.querySelector('input[name="id"]');
      this.priceEl = this.querySelector('[data-price]');
      this.compareEl = this.querySelector('[data-compare]');
      this.atcButton = this.querySelector('[data-atc]');
      this.atcLabel = this.querySelector('[data-atc-label]');
      this.includedEl = this.querySelector('[data-included]');
      this.imageEl = this.querySelector('[data-variant-image]');
      this.live = this.querySelector('[data-live]');
      this.optionInputs.forEach(i => i.addEventListener('change', () => this.update()));
      this.querySelectorAll('[data-qty-step]').forEach(b => b.addEventListener('click', e => this.step(parseInt(e.currentTarget.dataset.qtyStep, 10))));
      this.addEventListener('submit', e => this.submit(e));
      // In-card per-unit color dropdowns:
      //   - #1 in the selected card drives variant color (programmatically check the matching Color radio).
      //   - #2 / #3 ride along as line item properties on submit (no variant change).
      this.cardExtras = Array.from(this.querySelectorAll('[data-card-extras]'));
      this.cardSelects = Array.from(this.querySelectorAll('[data-unit-select]'));
      this.cardSelects.forEach(s => s.addEventListener('change', () => this.onUnitChange(s)));
      this.optionInputs.forEach(i => i.addEventListener('change', () => this.syncUnitDefaults()));
      this.syncUnitDefaults();
      this.update(true);
    }
    selectedOptions() {
      // Radio name is "options[OptionName]" (Shopify form convention).
      // Extract the option name so it matches the keys in our variant JSON.
      const map = {};
      this.optionInputs.forEach(i => {
        if (!i.checked) return;
        const m = i.name.match(/options\[(.+)\]/);
        const key = m ? m[1] : i.name;
        map[key] = i.value;
      });
      return map;
    }
    findVariant() {
      const sel = this.selectedOptions();
      return this.variants.find(v => Object.entries(sel).every(([k, val]) => v.options[k] === val));
    }
    update(initial) {
      const v = this.findVariant();
      if (!v) {
        if (this.atcButton) this.atcButton.disabled = true;
        if (this.atcLabel) this.atcLabel.textContent = 'Unavailable';
        return;
      }
      if (this.idInput) this.idInput.value = v.id;
      if (this.priceEl) this.priceEl.textContent = v.priceFormatted;
      // Effective compare baseline: real compare_at OR multi-pack fallback (N × first-variant price)
      const effectiveCompare = (v.compareAt && v.compareAt > v.price) ? v.compareAt : (v.compareAtFallback || 0);
      if (this.compareEl) {
        if (effectiveCompare > v.price) {
          this.compareEl.textContent = this.formatMoney(effectiveCompare);
          this.compareEl.hidden = false;
        } else {
          this.compareEl.hidden = true;
        }
      }
      // Save pill (header) — uses effective compare; sets both `hidden` attr AND a CSS class so style overrides can't show a stale value.
      const savePill = this.querySelector('[data-save-pill]');
      const saveAmount = this.querySelector('[data-save-amount]');
      if (savePill) {
        const diff = effectiveCompare - v.price;
        if (diff > 0) {
          savePill.hidden = false;
          savePill.classList.remove('is-hidden');
          if (saveAmount) saveAmount.textContent = this.formatMoney(diff);
        } else {
          savePill.hidden = true;
          savePill.classList.add('is-hidden');
        }
      }
      // ATC running total
      const atcTotal = this.querySelector('[data-atc-total]');
      if (atcTotal) atcTotal.textContent = this.formatMoney(v.price * this.qty());
      // Color label
      const colorLabel = this.querySelector('[data-color-label]');
      if (colorLabel) {
        const colorVal = Object.entries(this.selectedOptions()).find(([k]) => /^colou?r$/i.test(k));
        if (colorVal) colorLabel.textContent = colorVal[1];
      }
      // Bundle / package / kit label (legend "BUNDLE — [name]" stays in sync)
      const bundleLabel = this.querySelector('[data-bundle-label]');
      if (bundleLabel) {
        const bundleVal = Object.entries(this.selectedOptions()).find(([k]) => /^(bundle|package|kit)$/i.test(k));
        if (bundleVal) bundleLabel.textContent = bundleVal[1];
      }
      if (this.atcButton) this.atcButton.disabled = !v.available;
      if (this.atcLabel) this.atcLabel.textContent = v.available ? (this.atcLabel.dataset.add || 'Add to cart') : (this.atcLabel.dataset.soldOut || 'Sold out');
      if (this.includedEl && v.included) {
        // Safe DOM construction — avoid innerHTML XSS surface from merchant-typed variant data
        this.includedEl.replaceChildren(...v.included.map((i) => {
          const li = document.createElement('li');
          li.textContent = String(i);
          return li;
        }));
      }
      if (this.imageEl && v.image) {
        this.imageEl.src = v.image;
        if (v.imageSrcset) this.imageEl.srcset = v.imageSrcset;
        if (v.imageAlt) this.imageEl.alt = v.imageAlt;
      }
      if (this.live && !initial) this.live.textContent = `${v.priceFormatted}, ${v.available ? 'available' : 'sold out'}`;
      if (!initial) {
        const url = new URL(window.location);
        url.searchParams.set('variant', v.id);
        window.history.replaceState({}, '', url);
      }
      this.dispatchEvent(new CustomEvent('orreno:variant', { detail: { variant: v, qty: this.qty() }, bubbles: true }));
    }
    selectedBundleCardValue() {
      const sel = this.selectedOptions();
      const e = Object.entries(sel).find(([k]) => /^(bundle|package|kit)$/i.test(k));
      return e ? e[1] : null;
    }
    onUnitChange(sel) {
      sel.dataset.touched = '1';
      // Only the currently-selected card's #1 drives the variant color.
      const myCardValue = sel.dataset.cardValue;
      if (sel.dataset.unitSelect !== '1') return;
      if (myCardValue !== this.selectedBundleCardValue()) return;
      // Find the Color radio with matching value and check it.
      const target = sel.value;
      this.optionInputs.forEach(i => {
        if (/^options\[colou?r\]$/i.test(i.name) && i.value === target && !i.checked) {
          i.checked = true;
          i.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });
    }
    syncUnitDefaults() {
      // Default any UNTOUCHED unit select to the currently-selected main color.
      const sel = this.selectedOptions();
      const colorEntry = Object.entries(sel).find(([k]) => /^colou?r$/i.test(k));
      if (!colorEntry) return;
      const mainColor = colorEntry[1];
      this.cardSelects.forEach(s => {
        if (!s.dataset.touched) s.value = mainColor;
      });
    }
    formatMoney(cents) {
      // Prefer Shopify's money format if exposed; otherwise fall back to USD-ish
      try {
        if (window.Shopify && window.Shopify.formatMoney) {
          return window.Shopify.formatMoney(cents, window.Shopify.money_format || '${{amount}}');
        }
      } catch (e) {}
      return '$' + (cents / 100).toFixed(2).replace(/\.00$/, '');
    }
    qty() { return Math.max(1, parseInt(this.qtyInput?.value, 10) || 1); }
    step(delta) {
      if (!this.qtyInput) return;
      this.qtyInput.value = Math.max(1, this.qty() + delta);
      this.dispatchEvent(new CustomEvent('orreno:variant', { detail: { variant: this.findVariant(), qty: this.qty() }, bubbles: true }));
    }
    async submit(e) {
      e.preventDefault();
      if (this.atcButton) { this.atcButton.disabled = true; this.atcButton.classList.add('is-loading'); }
      try {
        // Use internal variant state — findVariant() resolves from current option-input selections.
        // This avoids relying on Shopify's form-renderer output (input names have changed across API versions).
        const v = this.findVariant();
        if (!v || !v.id) throw new Error('No variant selected');
        const variantId = v.id;
        const quantity = this.qty();
        // Per-unit colors → single visible "Colors" property (joined " · ") at any bundle qty.
        // We read from the SELECTED card's dropdowns (each select has data-card-value matching its parent card's option value).
        const sel = this.selectedOptions();
        const bundleEntry = Object.entries(sel).find(([k]) => /^(bundle|package|kit)$/i.test(k));
        let bundleQty = 1;
        if (bundleEntry) {
          const m = (bundleEntry[1] || '').match(/^(\d+)/);
          if (m) bundleQty = parseInt(m[1], 10);
        }
        const properties = {};
        if (bundleEntry) {
          const selectedCardValue = bundleEntry[1];
          const picks = [];
          this.cardSelects
            .filter(s => s.dataset.cardValue === selectedCardValue)
            .sort((a, b) => parseInt(a.dataset.unitSelect, 10) - parseInt(b.dataset.unitSelect, 10))
            .forEach(s => {
              const n = parseInt(s.dataset.unitSelect, 10);
              if (n <= bundleQty) picks.push(s.value);
            });
          if (picks.length) properties['Colors'] = picks.join(' · ');
        }
        const item = { id: variantId, quantity: quantity };
        if (Object.keys(properties).length) item.properties = properties;
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [item] })
        });
        const data = await res.json();
        if (data.status || data.description) throw new Error(data.description || data.message || 'Cart error');
        this.dispatchEvent(new CustomEvent('orreno:added', { detail: data, bubbles: true }));
        // Tracking pixel hook — Pixel/GA4 listen for this on document
        try {
          const items = data.items || (data.id ? [data] : []);
          const value = items.reduce((s, it) => s + (it.line_price || it.final_line_price || it.price || 0) / 100, 0);
          const ids = items.map((it) => String(it.product_id || it.id || ''));
          document.dispatchEvent(new CustomEvent('orreno:addtocart', {
            detail: {
              content_ids: ids,
              content_type: 'product',
              value: value,
              currency: (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD',
              num_items: items.length || 1
            }
          }));
        } catch (_) {}
        if (data.sections) this.swapSections(data.sections);
        document.dispatchEvent(new CustomEvent('cart:open'));
      } catch (err) {
        console.error('[orreno-buy-box] add error', err);
      } finally {
        if (this.atcButton) { this.atcButton.disabled = false; this.atcButton.classList.remove('is-loading'); this.update(true); }
      }
    }
    swapSections(sections) {
      Object.entries(sections).forEach(([id, html]) => {
        const target = document.getElementById(id) || document.querySelector(`[data-section-id="${id}"]`);
        if (!target || !html) return;
        const tmp = document.createElement('div'); tmp.innerHTML = html;
        const fresh = tmp.querySelector(`#${CSS.escape(id)}`) || tmp.firstElementChild;
        if (fresh) target.replaceWith(fresh);
      });
    }
  }

  // === <orreno-sticky-atc> — appears after hero scrolls out, hides over footer ===
  class OrrenoStickyAtc extends HTMLElement {
    connectedCallback() {
      const triggerSel = this.dataset.triggerSelector || '[data-sticky-trigger]';
      const trigger = document.querySelector(triggerSel);
      if (!trigger) return;
      this.priceEl = this.querySelector('[data-sticky-price]');
      this.qtyEl = this.querySelector('[data-sticky-qty]');
      this.titleEl = this.querySelector('[data-sticky-title]');
      this.hidden = true;
      this._heroIn = true;
      this._footerIn = false;
      const updateVisible = () => {
        // Visible when: hero scrolled out (heroIn=false) AND footer not in view (footerIn=false)
        const shouldShow = !this._heroIn && !this._footerIn;
        this.toggleAttribute('hidden', !shouldShow);
      };
      // Hero observer — sticky shows after hero leaves viewport
      new IntersectionObserver(entries => {
        entries.forEach(e => { this._heroIn = e.isIntersecting; });
        updateVisible();
      }, { threshold: 0, rootMargin: '0px 0px -80% 0px' }).observe(trigger);
      // Footer observer — sticky hides as footer comes into view (don't cover footer content)
      const footer = document.getElementById('orreno-footer') || document.querySelector('footer[role="contentinfo"]');
      if (footer) {
        new IntersectionObserver(entries => {
          entries.forEach(e => { this._footerIn = e.isIntersecting; });
          updateVisible();
        }, { threshold: 0, rootMargin: '0px 0px -80px 0px' }).observe(footer);
      }
      document.addEventListener('orreno:variant', e => {
        const v = e.detail.variant; if (!v) return;
        if (this.priceEl) this.priceEl.textContent = v.priceFormatted;
        if (this.qtyEl) this.qtyEl.textContent = `× ${e.detail.qty}`;
        if (this.titleEl && v.title) this.titleEl.textContent = v.title;
      });
      this.querySelector('[data-sticky-atc]')?.addEventListener('click', (e) => {
        e.preventDefault();
        const target = document.querySelector('orreno-buy-box [data-atc]')
                    || document.querySelector('[data-sticky-trigger]')
                    || document.querySelector('orreno-buy-box');
        if (target) {
          target.scrollIntoView({ behavior: 'smooth', block: 'center' });
          setTimeout(() => { try { target.focus?.({ preventScroll: true }); } catch (_) {} }, 600);
        }
      });
    }
  }

  // === <orreno-before-after> — UV reveal slider ===
  class OrrenoBeforeAfter extends HTMLElement {
    connectedCallback() {
      this.handle = this.querySelector('[data-handle]');
      this.afterImg = this.querySelector('[data-after]');
      this.value = parseInt(this.dataset.default || '50', 10);
      this.setAttribute('role', 'slider');
      this.setAttribute('tabindex', '0');
      this.setAttribute('aria-valuemin', '0');
      this.setAttribute('aria-valuemax', '100');
      this.setAttribute('aria-label', 'Before and after comparison');
      this.set(this.value);
      const move = e => {
        const rect = this.getBoundingClientRect();
        const x = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
        this.set(Math.max(0, Math.min(100, (x / rect.width) * 100)));
      };
      const start = () => {
        document.addEventListener('mousemove', move);
        document.addEventListener('touchmove', move, { passive: true });
        document.addEventListener('mouseup', stop, { once: true });
        document.addEventListener('touchend', stop, { once: true });
      };
      const stop = () => {
        document.removeEventListener('mousemove', move);
        document.removeEventListener('touchmove', move);
      };
      this.addEventListener('mousedown', start);
      this.addEventListener('touchstart', start, { passive: true });
      this.addEventListener('keydown', e => {
        if (e.key === 'ArrowLeft') { e.preventDefault(); this.set(this.value - 5); }
        if (e.key === 'ArrowRight') { e.preventDefault(); this.set(this.value + 5); }
        if (e.key === 'Home') { e.preventDefault(); this.set(0); }
        if (e.key === 'End') { e.preventDefault(); this.set(100); }
      });
    }
    set(v) {
      this.value = Math.max(0, Math.min(100, v));
      if (this.afterImg) this.afterImg.style.clipPath = `inset(0 ${100 - this.value}% 0 0)`;
      if (this.handle) this.handle.style.left = this.value + '%';
      this.setAttribute('aria-valuenow', Math.round(this.value));
    }
  }

  // === <orreno-faq> — accordion, single-open optional ===
  class OrrenoFaq extends HTMLElement {
    connectedCallback() {
      const single = this.dataset.single === 'true';
      this.querySelectorAll('details').forEach(d => {
        d.addEventListener('toggle', () => {
          if (single && d.open) {
            this.querySelectorAll('details').forEach(o => { if (o !== d) o.open = false; });
          }
        });
      });
    }
  }

  // === <orreno-tabs-gallery> — thumbnail tabs swap main image ===
  class OrrenoTabsGallery extends HTMLElement {
    connectedCallback() {
      const tabs = this.querySelectorAll('[role="tab"]');
      const panels = this.querySelectorAll('[role="tabpanel"]');
      tabs.forEach((t, i) => {
        t.addEventListener('click', () => this.activate(i));
        t.addEventListener('keydown', e => {
          if (e.key === 'ArrowRight') { e.preventDefault(); this.activate((i + 1) % tabs.length, true); }
          if (e.key === 'ArrowLeft') { e.preventDefault(); this.activate((i - 1 + tabs.length) % tabs.length, true); }
        });
      });
      this.tabs = tabs; this.panels = panels;
    }
    activate(idx, focus) {
      this.tabs.forEach((t, i) => {
        t.setAttribute('aria-selected', i === idx ? 'true' : 'false');
        t.tabIndex = i === idx ? 0 : -1;
        if (focus && i === idx) t.focus();
      });
      this.panels.forEach((p, i) => p.toggleAttribute('hidden', i !== idx));
    }
  }

  // === <orreno-page-nav> — pill jump-nav with smooth scroll + active state ===
  class OrrenoPageNav extends HTMLElement {
    resolveTarget(href) {
      if (!href || href === '#') return null;
      // Try direct id match first, then data-anchor match
      let t = document.querySelector(href);
      if (t) return t;
      const id = href.replace(/^#/, '');
      return document.querySelector(`[data-anchor="${CSS.escape(id)}"]`);
    }
    connectedCallback() {
      const tabs = Array.from(this.querySelectorAll('a[href^="#"]'));
      const targets = tabs.map(t => this.resolveTarget(t.getAttribute('href'))).filter(Boolean);
      tabs.forEach(t => t.addEventListener('click', e => {
        const target = this.resolveTarget(t.getAttribute('href'));
        if (target) {
          e.preventDefault();
          const offset = parseInt(this.dataset.scrollOffset, 10) || 88;
          const top = target.getBoundingClientRect().top + window.scrollY - offset;
          window.scrollTo({ top, behavior: 'smooth' });
          history.replaceState(null, '', t.getAttribute('href'));
        }
      }));
      if (!targets.length) return;
      const obs = new IntersectionObserver(entries => {
        entries.forEach(e => {
          if (e.isIntersecting) {
            const i = targets.indexOf(e.target);
            tabs.forEach((t, j) => t.toggleAttribute('aria-current', i === j));
          }
        });
      }, { rootMargin: '-30% 0px -55% 0px' });
      targets.forEach(t => obs.observe(t));
    }
  }

  // === <orreno-show-more> — progressive disclosure ===
  class OrrenoShowMore extends HTMLElement {
    connectedCallback() {
      this.items = Array.from(this.querySelectorAll('[data-show-more-item]'));
      this.btnMore = this.querySelector('[data-show-more]');
      this.btnLess = this.querySelector('[data-show-less]');
      this.countEl = this.querySelector('[data-show-more-count]');
      this.initial = parseInt(this.dataset.initial, 10) || 6;
      this.step = parseInt(this.dataset.step, 10) || 6;
      this.visible = this.initial;
      this.update();
      this.btnMore && this.btnMore.addEventListener('click', () => {
        this.visible = Math.min(this.items.length, this.visible + this.step);
        this.update();
      });
      this.btnLess && this.btnLess.addEventListener('click', () => {
        this.visible = this.initial;
        this.update();
        this.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    }
    update() {
      this.items.forEach((it, i) => it.toggleAttribute('hidden', i >= this.visible));
      if (this.btnMore) this.btnMore.toggleAttribute('hidden', this.visible >= this.items.length);
      if (this.btnLess) this.btnLess.toggleAttribute('hidden', this.visible <= this.initial);
      if (this.countEl) this.countEl.textContent = Math.max(0, this.items.length - this.visible);
    }
  }

  // === <orreno-gallery> — finger-swipe hero carousel + thumb/dot sync. Works desktop + mobile. ===
  class OrrenoGallery extends HTMLElement {
    connectedCallback() {
      this.heroList = this.querySelector('[data-hero-list]');
      if (!this.heroList) return;
      this.slides = Array.from(this.heroList.querySelectorAll('.bundle-bb__hero-slide'));
      this.thumbList = this.querySelector('[data-thumb-list]');
      this.thumbs = this.thumbList ? Array.from(this.thumbList.querySelectorAll('[data-thumb]')) : [];
      this.thumbItems = this.thumbList ? Array.from(this.thumbList.children) : [];
      this.dotsWrap = this.querySelector('[data-hero-dots]');
      this.dots = this.dotsWrap ? Array.from(this.dotsWrap.querySelectorAll('[data-hero-dot]')) : [];

      // Click thumb → scroll hero carousel to that slide
      this.thumbs.forEach((btn) => {
        btn.addEventListener('click', () => this.goTo(parseInt(btn.dataset.index, 10)));
      });
      this.dots.forEach((dot) => {
        dot.addEventListener('click', () => this.goTo(parseInt(dot.dataset.index, 10)));
      });

      // Sync active thumb/dot when user swipes/scrolls the hero
      if ('IntersectionObserver' in window && this.slides.length > 1) {
        const io = new IntersectionObserver((entries) => {
          entries.forEach((e) => {
            if (e.isIntersecting && e.intersectionRatio > 0.6) {
              const i = this.slides.indexOf(e.target);
              if (i >= 0) this.setActive(i);
            }
          });
        }, { root: this.heroList, threshold: [0.6, 0.8, 1] });
        this.slides.forEach((s) => io.observe(s));
      }

      // Desktop drag-to-scroll (mouse) — touch already works natively via overflow-x
      this.enableMouseDrag();
    }
    goTo(index) {
      const target = this.slides[index];
      if (!target) return;
      target.scrollIntoView({ behavior: 'smooth', inline: 'start', block: 'nearest' });
      this.setActive(index);
    }
    setActive(index) {
      this.thumbItems.forEach((li, i) => {
        const active = i === index;
        li.classList.toggle('is-active', active);
        const b = li.querySelector('[data-thumb]');
        if (!b) return;
        if (active) b.setAttribute('aria-current', 'true');
        else b.removeAttribute('aria-current');
      });
      this.dots.forEach((d, i) => {
        const active = i === index;
        d.classList.toggle('is-active', active);
        if (active) d.setAttribute('aria-current', 'true');
        else d.removeAttribute('aria-current');
      });
    }
    enableMouseDrag() {
      // Lets desktop users grab + drag the hero with the mouse, mirroring touch swipe.
      let isDown = false, startX = 0, scrollLeft = 0, hasMoved = false;
      const el = this.heroList;
      el.addEventListener('mousedown', (e) => {
        isDown = true; hasMoved = false;
        startX = e.pageX - el.offsetLeft; scrollLeft = el.scrollLeft;
        el.style.scrollBehavior = 'auto';
      });
      el.addEventListener('mouseleave', () => { isDown = false; el.style.scrollBehavior = ''; });
      el.addEventListener('mouseup', () => {
        isDown = false; el.style.scrollBehavior = '';
        // Snap to nearest slide
        if (hasMoved) {
          const w = el.clientWidth;
          const idx = Math.round(el.scrollLeft / w);
          this.goTo(idx);
        }
      });
      el.addEventListener('mousemove', (e) => {
        if (!isDown) return;
        e.preventDefault();
        const x = e.pageX - el.offsetLeft;
        const walk = (x - startX) * 1.2;
        if (Math.abs(walk) > 4) hasMoved = true;
        el.scrollLeft = scrollLeft - walk;
      });
    }
  }

  // === <orreno-compare-tabs> — deprecated; mobile compare now renders side-by-side without tabs ===
  // Kept as a no-op so any cached markup with this element doesn't error.
  class OrrenoCompareTabs extends HTMLElement {}

  // === <orreno-lazy-video> — facade for iframe / self-hosted video. Loads only when in viewport. ===
  // Solves: YouTube/Vimeo iframe with autoplay buffers immediately even when below the fold.
  class OrrenoLazyVideo extends HTMLElement {
    connectedCallback() {
      if (this._loaded) return;
      const load = () => this.activate();
      if (!('IntersectionObserver' in window)) { load(); return; }
      const io = new IntersectionObserver((entries, obs) => {
        entries.forEach(e => { if (e.isIntersecting) { obs.disconnect(); load(); } });
      }, { rootMargin: '200px 0px', threshold: 0.01 });
      io.observe(this);
    }
    activate() {
      if (this._loaded) return; this._loaded = true;
      const iframeSrc = this.dataset.iframeSrc;
      if (iframeSrc) {
        const f = document.createElement('iframe');
        f.src = iframeSrc;
        f.frameBorder = '0';
        f.allow = 'autoplay; encrypted-media; fullscreen; picture-in-picture';
        f.title = this.dataset.title || '';
        f.loading = 'lazy';
        // Replace the poster placeholder
        const poster = this.querySelector('.statement-slab-poster');
        if (poster) poster.replaceWith(f); else this.appendChild(f);
        return;
      }
      // Self-hosted <video> already in DOM with preload="metadata" — just trigger play
      const v = this.querySelector('video');
      if (v) {
        try { v.play().catch(() => {}); } catch (_) {}
      }
    }
  }

  customElements.define('orreno-buy-box', OrrenoBuyBox);
  customElements.define('orreno-sticky-atc', OrrenoStickyAtc);
  customElements.define('orreno-before-after', OrrenoBeforeAfter);
  customElements.define('orreno-faq', OrrenoFaq);
  customElements.define('orreno-tabs-gallery', OrrenoTabsGallery);
  customElements.define('orreno-page-nav', OrrenoPageNav);
  customElements.define('orreno-show-more', OrrenoShowMore);
  customElements.define('orreno-compare-tabs', OrrenoCompareTabs);
  customElements.define('orreno-gallery', OrrenoGallery);
  customElements.define('orreno-lazy-video', OrrenoLazyVideo);
  // Note: orreno-cart-drawer is defined inline in layout/theme.liquid (faster first paint, no separate fetch).
})();
